import { getApp } from '../../app';
import VueSocketIO from 'vue-3-socket.io';

/**
 * Flag to track if Vue Socket.IO plugin has been installed
 * @private
 * @type {boolean}
 */
let isVueSocketPluginInstalled = false;

/**
 * Integrates a given socket instance with the Vue 3 application.
 * This function installs the `svue-3-socket.io` plugin, making the socket
 * available globally in Vue components via `this.$socket` and connecting it to the
 * Vuex store for automatic state mutations.
 *
 * @param {object} socket - The active socket.io-client instance.
 * @param {object} store - The Vuex store instance.
 * @returns {boolean} True if plugin was installed/already installed, false if failed.
 */
export function installVueSocketIo(socket, store) {
	const app = getApp(); // Get the app instance from our helper

	if (!app) {
		console.error('Vue Integration: Vue app instance is not available. Make sure setApp() has been called.');
		return false;
	}

	if (!socket) {
		console.error('Vue Integration: Provided socket is invalid. Integration skipped.');
		return false;
	}

	if (!store) {
		console.error('Vue Integration: Vuex store is required for socket integration.');
		return false;
	}

	// Check if plugin is already installed using our flag
	if (isVueSocketPluginInstalled) {
		console.log('Vue Integration: Socket.IO plugin already installed.');
		return true;
	}

	// Double-check using the app's global properties (fallback safety check)
	if (app.config.globalProperties.$socket) {
		console.log('Vue Integration: Socket.IO plugin already installed (detected via globalProperties).');
		isVueSocketPluginInstalled = true;
		return true;
	}

	try {
		// vue-3-socket.io replaces socket.onevent entirely (without calling the original),
		// which silently kills all socket.on() listeners (e.g. Pinia stores using bindSocket).
		// Capture the original before the plugin overwrites it so we can restore the chain.
		const originalOnevent = socket.onevent?.bind(socket);

		// Install the plugin
		app.use(
			new VueSocketIO({
				debug: import.meta.env.NODE_ENV === 'development',
				connection: socket,
				vuex: {
					store,
					actionPrefix: 'socket_',
					mutationPrefix: 'socket_',
				},
			})
		);

		// Compose VueSocketIO's replacement with the original so both Vuex and
		// direct socket.on() listeners (Pinia) continue to receive events.
		if (originalOnevent && socket.onevent !== originalOnevent) {
			const vuexOnevent = socket.onevent.bind(socket);
			socket.onevent = (packet) => {
				vuexOnevent(packet);
				originalOnevent(packet);
			};
		}

		isVueSocketPluginInstalled = true;
		console.log('Vue Integration: Socket.IO plugin installed successfully.');
		return true;
	} catch (error) {
		console.error('Vue Integration: Failed to install Socket.IO plugin.', error);
		return false;
	}
}

/**
 * Reset the plugin installation flag (useful for testing or special cases)
 * @private
 */
export function resetVueSocketPluginState() {
	isVueSocketPluginInstalled = false;
}
